import type { ReactNode } from "react";
import { Check, CheckCheck, Clock, X } from "lucide-react";
import { formatChatMessageTime } from "@/lib/chat-date-format";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

/** Traduz erros técnicos de envio para um texto simples para o usuário final. */
function simplifyFailureMessage(friendlyMessage?: string | null): string {
  const raw = (friendlyMessage || "").trim();
  const e = raw.toLowerCase();
  if (!raw) return "A mensagem não foi enviada. Tente reenviar.";
  if (e.includes("tempo limite") || e.includes("timeout") || e.includes("timed out")) {
    return "O WhatsApp demorou para responder e a mensagem pode não ter sido entregue. Tente reenviar.";
  }
  if (e.includes("not on whatsapp")) return "Este número não tem WhatsApp.";
  if (e.includes("disconnect") || e.includes("desconect") || e.includes("not connected")) {
    return "A conexão do WhatsApp estava desconectada no momento do envio. Reconecte e tente reenviar.";
  }
  if (e.includes("463")) return "O WhatsApp bloqueou o envio temporariamente. Aguarde e tente reenviar.";
  if (e.includes("indisponível") || e.includes("unavailable") || e.includes("rede")) {
    return "O serviço do WhatsApp estava indisponível. Tente reenviar em instantes.";
  }
  return raw;
}

type MessageTimestampMetaProps = {
  createdAt: string;
  isUserMessage: boolean;
  deliveryStatus?: string | null;
  friendlyMessage?: string | null;
  layout?: "inline" | "block";
  audioTranscriptionSlot?: ReactNode;
};

export function MessageTimestampMeta({
  createdAt,
  isUserMessage,
  deliveryStatus,
  friendlyMessage,
  layout = "block",
  audioTranscriptionSlot,
}: MessageTimestampMetaProps) {
  const isInline = layout === "inline";
  const Wrapper = isInline ? "span" : "div";

  return (
    <Wrapper
      className={cn(
        "text-[10px] sm:text-xs leading-none",
        isInline
          ? "inline-flex items-center gap-0.5 float-right ml-2 mt-[3px] -mb-[5px] h-[15px] shrink-0"
          : "mt-1 flex items-center gap-1 flex-wrap min-w-0 max-w-full",
        isUserMessage
          ? isInline
            ? "chat-bubble-user-fg/80 opacity-90"
            : "justify-end chat-bubble-user-fg/80 opacity-90"
          : isInline
            ? "opacity-70"
            : "justify-start opacity-70",
      )}
    >
      <span className="whitespace-nowrap">{formatChatMessageTime(createdAt)}</span>
      {audioTranscriptionSlot}
      {isUserMessage && deliveryStatus === "failed" ? (
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex items-center text-destructive opacity-100 cursor-help">
                <X className="w-3.5 h-3.5" strokeWidth={2.5} />
              </span>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-[260px] text-center">
              {simplifyFailureMessage(friendlyMessage)}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : isUserMessage && deliveryStatus === "pending" ? (
        <span title="Enviando..." className="inline-flex items-center">
          <Clock className="w-3.5 h-3.5 chat-bubble-user-fg" />
        </span>
      ) : isUserMessage ? (
        deliveryStatus === "delivered" || deliveryStatus === "read" ? (
          <CheckCheck
            className="w-3.5 h-3.5 box-content text-[#B5D900]"
            strokeWidth={2.25}
            aria-label={deliveryStatus === "read" ? "Lida" : "Entregue"}
          />
        ) : (
          <Check
            className="w-3.5 h-3.5 box-content text-[#B5D900]"
            strokeWidth={2.25}
            aria-label="Enviada"
          />
        )
      ) : null}
    </Wrapper>
  );
}
